import React, { useState, useEffect } from "react";
import { getBlockedAddresses, getBlockOptions, deleteBlockedAddress, getBlockedListExcel } from "../services/addressService";
import { formatDate } from "../helpers/stringHelpers";
import Pagination from "../components/Pagination";
import { SearchIcon, DeleteIcon, TakeBackIcon } from "../assets/icons";

export default function BlockedAddresses() {
    const [blockedAddresses, setBlockedAddresses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [blockOptions, setBlockOptions] = useState([]);
    
    // Filter states
    const [selectedType, setSelectedType] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDate, setSelectedDate] = useState("");

    const fetchBlockedAddresses = async (filters = {}) => {
        try {
            const params = {
                count: 15, 
                offset: filters.resetPage ? 0 : currentPage,
            };

            // Build filters object from current state and passed filters
            const currentFilters = {};
            if (selectedType) currentFilters.type = selectedType;
            if (selectedDate) currentFilters.date = selectedDate;
            if (searchTerm.trim()) currentFilters.search = searchTerm.trim();
            
            // Merge current filters with passed filters (passed filters take precedence)
            const finalFilters = { ...currentFilters, ...filters };
            
            // Add filter parameters to params
            if (finalFilters.type) params.type = finalFilters.type;
            if (finalFilters.date) params.date = finalFilters.date;
            if (finalFilters.search) params.search = finalFilters.search;
            
            const response = await getBlockedAddresses(params);
            
            setBlockedAddresses(response.data);
            setTotalPages(Math.ceil(response.totalRecords / 15));
            
            // Reset to page 0 if filters were applied
            if (filters.resetPage) {
                setCurrentPage(0);
            }
        } catch (error) {
            setError(error.message || 'Veri yükleme hatası oluştu');
        } finally {
            setLoading(false);
        }
    }

    const fetchBlockOptions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getBlockOptions();
            setBlockOptions(response.data);
        } catch (error) {
            setError(error.message || 'Blok seçenekleri yükleme hatası oluştu');
        } finally {
            setLoading(false);
        }
    }

    const handleDeleteBlockedAddress = async (id) => {
        try {
            await deleteBlockedAddress(id);
            fetchBlockedAddresses({});
        } catch (error) {
            setError(error.message || 'Silme işlemi hatası oluştu');
        }
    }

    // Handle type selection
    const handleTypeChange = (e) => {
        const type = e.target.value;
        setSelectedType(type);
        fetchBlockedAddresses({ type, resetPage: true });
    };

    // Handle date selection
    const handleDateChange = (e) => {
        const date = e.target.value;
        setSelectedDate(date);
        fetchBlockedAddresses({ date, resetPage: true });
    };

    // Handle search
    const handleSearch = () => {
        fetchBlockedAddresses({ search: searchTerm.trim(), resetPage: true });
    };

    // Handle search on Enter key
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleExcelDownload = async () => {
        try {
            // Build params object only with values that exist
            const params = {};
            if (selectedType) params.type = selectedType;
            if (selectedDate) params.date = selectedDate;
            if (searchTerm.trim()) params.search = searchTerm.trim();
            
            const response = await getBlockedListExcel(params);
            
            // Convert response to blob
            const blob = await response.blob();
            
            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'bloklanan-adresler.xlsx';
            
            // Trigger the download
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Excel download error:', error);
            setError(error.message || 'Excel indirme hatası oluştu');
        }
    }

    useEffect(() => {
        fetchBlockOptions();
    }, []);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetchBlockedAddresses();
    }, [currentPage]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="blocked-addresses-container">
            <div className="search-container">
                <div className="search-inputs">
                    {/* Type dropdown */}
                    <div className="search-input-group">
                        <select 
                            value={selectedType} 
                            onChange={handleTypeChange}
                            className="search-select"
                        >
                            <option value="">Tümü</option>
                            {blockOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Search input for address */}
                    <div className="search-input-group">
                        <input
                            type="text"
                            placeholder="Adres ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            className="search-input"
                        />
                        <button 
                            onClick={handleSearch}
                            className="search-button"
                        >
                            <SearchIcon className="search-icon" />
                        </button>
                    </div>

                    {/* Date selector */}
                    <div className="search-input-group">
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            className="search-date-input"
                        />
                    </div>
                    <button className="download-excel-button" onClick={handleExcelDownload}>
                        Excel İndir
                    </button>
                </div>
            </div>
            <div className="table-container">
                <table className="personel-table">
                    <thead>
                        <tr>
                            <th>Adres</th>
                            <th>Bloklandığı Tarih</th>
                            <th>Kullanıcı</th>
                            <th>Açıklama</th>
                            <th>Tipi</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {blockedAddresses.map((address) => (
                            <tr key={address.blockId}>
                                <td className={!address.isActive ? 'deleted' : ''}>{address.fullAdress}</td>
                                <td className={!address.isActive ? 'deleted' : ''}>{formatDate(address.addedDate)}</td>
                                <td className={!address.isActive ? 'deleted' : ''}>{address.userName}</td>
                                <td className={!address.isActive ? 'deleted' : ''}>{address.description}</td>
                                <td className={!address.isActive ? 'deleted' : ''}>{address.optionDesc}</td>
                                <td>
                                    {address.isActive ? (
                                        <button className="icon-button delete" onClick={() => handleDeleteBlockedAddress(address.blockId)}>
                                            Sil
                                            <DeleteIcon color='#C70000' />
                                        </button>
                                    ) : (
                                        <button className="complete-button takeback-button" onClick={() => handleDeleteBlockedAddress(address.blockId)}>
                                            Geri Al
                                            <TakeBackIcon color='#000000' />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />
        </div>
    );
}